'use client';

import React, { useState } from 'react';
import { Search, Filter, ChevronUp, ChevronDown, Eye } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import type { Credential, CredentialStatus } from '../page';

interface LicenseInventoryProps {
  credentials: Credential[];
  search: string;
  onSearchChange: (v: string) => void;
  filterStatus: CredentialStatus | 'all';
  onFilterStatus: (v: CredentialStatus | 'all') => void;
  filterDept: string;
  onFilterDept: (v: string) => void;
  filterCategory: Credential['category'] | 'all';
  onFilterCategory: (v: Credential['category'] | 'all') => void;
}

type SortKey = 'staffName' | 'credentialName' | 'expiryDate' | 'daysUntilExpiry' | 'status';

function statusBadgeVariant(s: CredentialStatus): 'active' | 'pending' | 'expired' | 'default' {
  if (s === 'active') return 'active';
  if (s === 'expired') return 'expired';
  if (s === 'pending_renewal') return 'pending';
  return 'default';
}

function statusLabel(s: CredentialStatus) {
  if (s === 'active') return 'Active';
  if (s === 'expiring_soon') return 'Expiring Soon';
  if (s === 'expired') return 'Expired';
  return 'Pending Renewal';
}

function ExpiryCountdown({ days }: { days: number }) {
  if (days < 0) return <span className="text-xs font-600 text-danger">{Math.abs(days)}d overdue</span>;
  if (days === 0) return <span className="text-xs font-600 text-danger">Expires today</span>;
  if (days <= 7) return <span className="text-xs font-600 text-danger">{days}d left</span>;
  if (days <= 30) return <span className="text-xs font-600 text-warning">{days}d left</span>;
  if (days <= 90) return <span className="text-xs font-500 text-foreground">{days}d left</span>;
  const months = Math.floor(days / 30);
  return <span className="text-xs text-muted-foreground">{months}mo left</span>;
}

const DEPARTMENTS = ['ICU', 'Med-Surg', 'Emergency', 'Pediatrics', 'Cardiology', 'Oncology'];
const CATEGORIES: Credential['category'][] = ['license', 'certification', 'training', 'background'];
const STATUSES: { value: CredentialStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'expiring_soon', label: 'Expiring Soon' },
  { value: 'expired', label: 'Expired' },
  { value: 'pending_renewal', label: 'Pending Renewal' },
];

export default function LicenseInventory({
  credentials, search, onSearchChange,
  filterStatus, onFilterStatus,
  filterDept, onFilterDept,
  filterCategory, onFilterCategory,
}: LicenseInventoryProps) {
  const [sortKey, setSortKey] = useState<SortKey>('daysUntilExpiry');
  const [sortAsc, setSortAsc] = useState(true);
  const [selected, setSelected] = useState<Credential | null>(null);

  const sorted = [...credentials].sort((a, b) => {
    let av: string | number = a[sortKey] as string | number;
    let bv: string | number = b[sortKey] as string | number;
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortAsc ? -1 : 1;
    if (av > bv) return sortAsc ? 1 : -1;
    return 0;
  });

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  }

  function SortIcon({ k }: { k: SortKey }) {
    if (sortKey !== k) return <ChevronUp size={12} className="text-muted-foreground/40" />;
    return sortAsc ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search staff, credential, license #..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => onFilterStatus(e.target.value as CredentialStatus | 'all')}
            className="px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none text-foreground"
          >
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={filterDept}
            onChange={(e) => onFilterDept(e.target.value)}
            className="px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none text-foreground"
          >
            <option value="all">All Departments</option>
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => onFilterCategory(e.target.value as Credential['category'] | 'all')}
            className="px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none text-foreground capitalize"
          >
            <option value="all">All Types</option>
            {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
          </select>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Filter size={13} />
            <span>{credentials.length} records</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3">
                  <button onClick={() => toggleSort('staffName')} className="flex items-center gap-1 text-xs font-600 text-muted-foreground hover:text-foreground">
                    Staff <SortIcon k="staffName" />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button onClick={() => toggleSort('credentialName')} className="flex items-center gap-1 text-xs font-600 text-muted-foreground hover:text-foreground">
                    Credential <SortIcon k="credentialName" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">License #</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Issuing Body</th>
                <th className="text-left px-4 py-3">
                  <button onClick={() => toggleSort('expiryDate')} className="flex items-center gap-1 text-xs font-600 text-muted-foreground hover:text-foreground">
                    Expiry <SortIcon k="expiryDate" />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button onClick={() => toggleSort('daysUntilExpiry')} className="flex items-center gap-1 text-xs font-600 text-muted-foreground hover:text-foreground">
                    Countdown <SortIcon k="daysUntilExpiry" />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button onClick={() => toggleSort('status')} className="flex items-center gap-1 text-xs font-600 text-muted-foreground hover:text-foreground">
                    Status <SortIcon k="status" />
                  </button>
                </th>
                <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">No credentials match your filters.</td>
                </tr>
              )}
              {sorted.map((c) => (
                <tr key={c.id} className={`hover:bg-muted/20 transition-colors ${c.status === 'expired' ? 'bg-danger/5' : c.status === 'expiring_soon' ? 'bg-warning/5' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[10px] font-700 flex items-center justify-center flex-shrink-0">
                        {c.staffName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <p className="font-500 text-foreground text-xs">{c.staffName}</p>
                        <p className="text-[10px] text-muted-foreground">{c.role} · {c.department}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-500 text-foreground">{c.credentialName}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{c.category}</p>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{c.licenseNumber}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{c.issuingBody}</td>
                  <td className="px-4 py-3 text-xs text-foreground">{c.expiryDate}</td>
                  <td className="px-4 py-3"><ExpiryCountdown days={c.daysUntilExpiry} /></td>
                  <td className="px-4 py-3">
                    {c.status === 'expiring_soon' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-600 bg-warning/10 text-warning">
                        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                        Expiring Soon
                      </span>
                    ) : (
                      <Badge variant={statusBadgeVariant(c.status)} dot>{statusLabel(c.status)}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setSelected(c)}
                      className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="View details"
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Credential Details" size="md">
        {selected && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-border">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary text-sm font-700 flex items-center justify-center">
                {selected.staffName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <p className="font-600 text-foreground">{selected.staffName}</p>
                <p className="text-sm text-muted-foreground">{selected.role} · {selected.department}</p>
              </div>
              <div className="ml-auto">
                {selected.status === 'expiring_soon' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-600 bg-warning/10 text-warning">
                    Expiring Soon
                  </span>
                ) : (
                  <Badge variant={statusBadgeVariant(selected.status)} dot>{statusLabel(selected.status)}</Badge>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Credential', value: selected.credentialName },
                { label: 'Type', value: selected.credentialType },
                { label: 'Category', value: selected.category },
                { label: 'License #', value: selected.licenseNumber },
                { label: 'Issuing Body', value: selected.issuingBody },
                { label: 'Issue Date', value: selected.issueDate },
                { label: 'Expiry Date', value: selected.expiryDate },
                { label: 'Days Until Expiry', value: selected.daysUntilExpiry < 0 ? `${Math.abs(selected.daysUntilExpiry)} days overdue` : `${selected.daysUntilExpiry} days` },
              ].map((row) => (
                <div key={row.label} className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">{row.label}</p>
                  <p className="text-sm font-500 text-foreground capitalize">{row.value}</p>
                </div>
              ))}
            </div>
            {selected.renewalSubmitted && (
              <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg text-sm text-primary font-500">
                <span>✓</span> Renewal application submitted
              </div>
            )}
            {selected.notes && (
              <div className="px-3 py-2 bg-muted/40 rounded-lg text-sm text-muted-foreground">
                <span className="font-600 text-foreground">Notes: </span>{selected.notes}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-600 hover:opacity-90 transition-opacity">
                Mark Renewal Submitted
              </button>
              <button onClick={() => setSelected(null)} className="px-4 py-2 rounded-lg border border-border text-sm font-600 text-muted-foreground hover:text-foreground transition-colors">
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
